import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { trackEvent } from '../lib/analytics';

interface MagicSearchProps {
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
  containerClassName?: string;
}

export interface MagicSearchRef {
  focus: () => void;
}

const MagicSearch = forwardRef<MagicSearchRef, MagicSearchProps>(({ value, onChange, debounceMs = 300, containerClassName }, ref) => {
  const { t } = useLanguage();
  const [isActive, setIsActive] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    }
  }));

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
        const trimmed = localValue.trim();
        if (trimmed.length === 0) {
          trackEvent('search_clear', { via: 'typing' });
        } else {
          trackEvent('search', { query_length: Math.min(trimmed.length, 200) });
        }
      }
    }, debounceMs);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [localValue, debounceMs, onChange, value]);

  const handleClear = () => {
    trackEvent('search_clear', { via: 'button' });
    setLocalValue('');
    onChange('');
    setIsActive(false);
  };

  const mergedContainerClassName = containerClassName ?? 'max-w-3xl mx-auto mb-10 md:mb-16';

  return (
    <div className={`relative w-full z-20 group ${mergedContainerClassName}`}>
      {/* Removed Glow Effect Div */}
      
      <div className={`
        relative rounded-xl transition-all duration-300 bg-white dark:bg-background/50 backdrop-blur-md p-0.5 ring-1 ring-slate-200 dark:ring-white/10
        ${isActive ? 'ring-primary/50' : ''}
      `}>
        
        <div className="relative bg-white dark:bg-surface/50 rounded-lg flex items-center overflow-hidden h-9 md:h-10">
          <div className="pl-3 text-slate-400 dark:text-slate-400">
             <svg className={`w-5 h-5 transition-colors ${isActive ? 'text-primary' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
             </svg>
          </div>
          
          <input
            ref={inputRef}
            type="text"
            className="w-full bg-transparent border-none px-3 text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-0 focus:outline-none text-sm md:text-base"
            placeholder={t.search_placeholder}
            aria-label={t.search_placeholder}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onFocus={() => setIsActive(true)}
            onBlur={() => !localValue && setIsActive(false)}
          />
          
          {localValue && (
             <button
               type="button"
               onClick={handleClear}
               aria-label="Clear search"
               className="p-1.5 text-slate-400 hover:text-slate-800 dark:text-slate-500 dark:hover:text-white transition-colors mr-1.5"
             >
               <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
               </svg>
             </button>
          )}
        </div>
      </div>
    </div>
  );
});

MagicSearch.displayName = 'MagicSearch';

export default MagicSearch;
